import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  User,
  Clock,
  Play,
  Pause,
  Coffee,
  RefreshCw,
  ArrowRightLeft,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';

interface OperatorStatus {
  id: string;
  operator_id: string;
  status: 'available' | 'working' | 'break' | 'offline';
  current_work_order_id: string | null;
  current_process_started_at: string | null;
  last_activity_at: string | null;
  operator: {
    full_name: string;
  };
  current_work_order?: {
    id: string;
    order_number: string;
    product_name: string;
    is_paused: boolean;
    paused_at: string | null;
  };
}

interface CompletedToday {
  operator_id: string;
  count: number;
}

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  planning_status: string;
}

const SCHEDULING_TABS = [
  { value: 'calendario', label: 'Calendario', href: '/schedule' },
  { value: 'planificador', label: 'Planificador', href: '/production-planner' },
  { value: 'gantt', label: 'Gantt', href: '/gantt-planner' },
  { value: 'kanban', label: 'Kanban', href: '/production-kanban' },
  { value: 'operarios', label: 'Operarios', href: '/operators-management' },
];

export default function OperatorsManagement() {
  const { toast } = useToast();
  const [operators, setOperators] = useState<OperatorStatus[]>([]);
  const [completedToday, setCompletedToday] = useState<CompletedToday[]>([]);
  const [availableOrders, setAvailableOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<OperatorStatus | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [pauseCurrentOrder, setPauseCurrentOrder] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchOperators = async () => {
    try {
      const { data, error } = await api
        .from('operator_workload')
        .select('*,profiles(full_name),current_work_order:work_orders(id,order_number,product_name,is_paused,paused_at)')
        .order('status');

      if (error) throw error;
      setOperators(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar el estado de operarios',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedToday = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data } = await api
      .from('process_records')
      .select('operator_id')
      .eq('status', 'completed')
      .gte('completed_at', today.toISOString());

    if (data) {
      const counts: Record<string, number> = {};
      data.forEach((r: any) => {
        if (r.operator_id) {
          counts[r.operator_id] = (counts[r.operator_id] || 0) + 1;
        }
      });
      setCompletedToday(
        Object.entries(counts).map(([operator_id, count]) => ({ operator_id, count }))
      );
    }
  };

  const fetchAvailableOrders = async () => {
    const { data } = await api
      .from('work_orders')
      .select('id, order_number, product_name, planning_status')
      .in('planning_status', ['pending', 'scheduled', 'in_production'])
      .order('created_at', { ascending: false })
      .limit(50);

    setAvailableOrders(data || []);
  };

  useEffect(() => {
    fetchOperators();
    fetchCompletedToday();
    fetchAvailableOrders();
    const interval = setInterval(() => {
      fetchOperators();
      fetchCompletedToday();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string, isPaused?: boolean) => {
    if (isPaused) return <Pause className="w-5 h-5 text-amber-500" />;
    switch (status) {
      case 'available': return <User className="w-5 h-5 text-green-500" />;
      case 'working': return <Play className="w-5 h-5 text-blue-500" />;
      case 'break': return <Coffee className="w-5 h-5 text-amber-500" />;
      default: return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string, isPaused?: boolean) => {
    if (isPaused) {
      return <Badge className="bg-amber-500 text-white"><Pause className="w-3 h-3 mr-1" />Pausado</Badge>;
    }
    const config: Record<string, { label: string; className: string }> = {
      available: { label: 'Disponible', className: 'bg-green-500 text-white' },
      working: { label: 'Trabajando', className: 'bg-blue-500 text-white' },
      break: { label: 'Descanso', className: 'bg-amber-500 text-white' },
      offline: { label: 'Offline', className: 'bg-gray-500 text-white' },
    };
    const c = config[status] || config.offline;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getCompletedCount = (operatorId: string) => {
    return completedToday.find(c => c.operator_id === operatorId)?.count || 0;
  };

  const openReassignDialog = (operator: OperatorStatus) => {
    setSelectedOperator(operator);
    setSelectedOrderId('');
    setPauseCurrentOrder(true);
    setReassignDialogOpen(true);
  };

  const handleReassign = async () => {
    if (!selectedOperator || !selectedOrderId) return;

    setSubmitting(true);
    try {
      // If operator has current order and we should pause it
      if (selectedOperator.current_work_order_id && pauseCurrentOrder) {
        await api
          .from('work_orders')
          .update({ is_paused: true, paused_at: new Date().toISOString() })
          .eq('id', selectedOperator.current_work_order_id);
      }

      // Assign operator to new order
      await api
        .from('work_orders')
        .update({
          assigned_operator: selectedOperator.operator_id,
          is_paused: false,
          paused_at: null,
        })
        .eq('id', selectedOrderId);

      // Update operator workload
      await api
        .from('operator_workload')
        .update({
          current_work_order_id: selectedOrderId,
          status: 'working',
        })
        .eq('operator_id', selectedOperator.operator_id);

      toast({
        title: 'Operario reasignado',
        description: `${selectedOperator.profiles?.full_name} asignado a nueva OT`,
      });

      setReassignDialogOpen(false);
      fetchOperators();
      fetchAvailableOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo reasignar',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const availableOperators = operators.filter(o => o.status === 'available');
  const workingOperators = operators.filter(o => o.status === 'working');
  const pausedOperators = workingOperators.filter(o => o.current_work_order?.is_paused);
  const breakOperators = operators.filter(o => o.status === 'break');

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <ModuleTabs tabs={SCHEDULING_TABS} />

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4 bg-green-500/10 border-green-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/20">
                <User className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-600">{availableOperators.length}</p>
                <p className="text-sm text-muted-foreground">Disponibles</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-blue-500/10 border-blue-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Play className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">{workingOperators.length}</p>
                <p className="text-sm text-muted-foreground">Trabajando</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-amber-500/10 border-amber-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/20">
                <Pause className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-600">{pausedOperators.length}</p>
                <p className="text-sm text-muted-foreground">Pausados</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gray-500/10 border-gray-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-gray-500/20">
                <Coffee className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-600">{breakOperators.length}</p>
                <p className="text-sm text-muted-foreground">Descanso</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Alerts */}
        {availableOperators.length > 0 && (
          <Card className="p-4 bg-amber-500/10 border-amber-500/30">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <span className="font-medium text-amber-700 dark:text-amber-400">
                {availableOperators.length} operario{availableOperators.length > 1 ? 's' : ''} sin asignar:
              </span>
              <span className="text-amber-600 dark:text-amber-500">
                {availableOperators.map(o => o.profiles?.full_name).join(', ')}
              </span>
            </div>
          </Card>
        )}

        {/* Operator Grid */}
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              Gestión de Operarios
            </h2>
            <Button variant="outline" size="sm" onClick={fetchOperators}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
          <ScrollArea className="h-[calc(100vh-380px)]">
            <div className="p-4 space-y-3">
              {operators.map((op) => (
                <Card
                  key={op.id}
                  className={`p-4 transition-colors ${
                    op.current_work_order?.is_paused ? 'border-amber-500/50 bg-amber-500/5' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-full bg-muted">
                        {getStatusIcon(op.status, op.current_work_order?.is_paused)}
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{op.profiles?.full_name || 'Sin nombre'}</p>
                        {op.current_work_order ? (
                          <Link
                            to={`/work-orders?open=${op.current_work_order.id}`}
                            className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
                          >
                            <span className="font-mono">{op.current_work_order.order_number}</span>
                            <span>- {op.current_work_order.product_name}</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (
                          <p className="text-sm text-muted-foreground">Sin OT asignada</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Time in process */}
                      {op.current_process_started_at && !op.current_work_order?.is_paused && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">En proceso</p>
                          <p className="font-mono text-sm">
                            {formatDistanceToNow(new Date(op.current_process_started_at), { locale: es })}
                          </p>
                        </div>
                      )}

                      {/* Paused time */}
                      {op.current_work_order?.is_paused && op.current_work_order.paused_at && (
                        <div className="text-right">
                          <p className="text-xs text-amber-500">Pausado hace</p>
                          <p className="font-mono text-sm text-amber-600">
                            {formatDistanceToNow(new Date(op.current_work_order.paused_at), { locale: es })}
                          </p>
                        </div>
                      )}

                      {/* Completed today */}
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Completados hoy</p>
                        <p className="font-semibold flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {getCompletedCount(op.operator_id)}
                        </p>
                      </div>

                      {/* Status badge */}
                      {getStatusBadge(op.status, op.current_work_order?.is_paused)}

                      {/* Reassign button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReassignDialog(op)}
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-1" />
                        Reasignar
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </Card>
      </div>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reasignar Operario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Operario</p>
              <p className="font-semibold">{selectedOperator?.profiles?.full_name}</p>
            </div>

            {selectedOperator?.current_work_order && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">OT actual</p>
                <p className="font-mono">{selectedOperator.current_work_order.order_number}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="pause-current"
                    checked={pauseCurrentOrder}
                    onCheckedChange={(v) => setPauseCurrentOrder(v as boolean)}
                  />
                  <label htmlFor="pause-current" className="text-sm">
                    Pausar OT actual automáticamente
                  </label>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-muted-foreground mb-2">Nueva OT</p>
              <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una OT" />
                </SelectTrigger>
                <SelectContent>
                  {availableOrders
                    .filter(o => o.id !== selectedOperator?.current_work_order_id)
                    .map((order) => (
                      <SelectItem key={order.id} value={order.id}>
                        <span className="font-mono">{order.order_number}</span>
                        <span className="text-muted-foreground ml-2">
                          {order.product_name?.substring(0, 30)}...
                        </span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {order.planning_status}
                        </Badge>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReassign} disabled={!selectedOrderId || submitting}>
              {submitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Reasignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
